import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Loader2, Globe, DollarSign, Eye, EyeOff } from 'lucide-react';
import { supabase, isDemoMode } from '@/lib/supabase';
import { currencies, languages } from '@/types';
import { toast } from 'sonner';
import { useLocationCurrency } from '@/hooks/useLocationCurrency';
import { sendWelcomeNotification } from '@/lib/notifications';

export default function OrgSignup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { currency: detectedCurrency, loading: currencyLoading } = useLocationCurrency('EUR');

  const [formData, setFormData] = useState({
    orgName: '',
    orgDescription: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    currency: 'EUR',
    language: 'en',
    monthlyDues: '',
  });

  useEffect(() => {
    if (!currencyLoading) {
      setFormData((prev) => ({ ...prev, currency: detectedCurrency }));
    }
  }, [detectedCurrency, currencyLoading]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isDemoMode) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const joinCode = Math.random().toString(36).substring(2, 10).toUpperCase();
        toast.success(t('auth.registerSuccess'));
        toast.info(`Demo mode — your join code would be: ${joinCode}`);
        navigate('/org-login');
        return;
      }

      // 1. Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.adminEmail,
        password: formData.adminPassword,
        options: {
          data: {
            full_name: formData.adminName,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('User creation failed');

      // If email confirmation is required, there will be no session yet.
      // Inform the user and skip DB inserts — they will be completed after login.
      if (!authData.session) {
        toast.success('Account created! Please check your email to verify your account, then sign in.');
        navigate('/org-login');
        return;
      }

      // 2. Create the organization with join_code
      const joinCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.orgName,
          description: formData.orgDescription,
          currency: formData.currency,
          language: formData.language,
          monthly_dues_amount: parseFloat(formData.monthlyDues) || 0,
          join_code: joinCode,
          created_by: authData.user.id,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // 3. Create user_organizations entry with admin role
      const { error: userOrgError } = await supabase.from('user_organizations').insert({
        user_id: authData.user.id,
        organization_id: orgData.id,
        role: 'admin',
        is_active: true,
      });

      if (userOrgError) throw userOrgError;

      await sendWelcomeNotification(authData.user.id, orgData.id, formData.adminName);

      toast.success(t('auth.registerSuccess'));
      toast.info(`Your organization join code is: ${joinCode}`);
      navigate('/org-login');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.registerError');
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold">{t('common.appName')}</span>
          </Link>
          <p className="mt-2 text-muted-foreground">{t('public.home.orgs.title')}</p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">
              {t('auth.createAccount')}
            </CardTitle>
            <CardDescription className="text-center">
              Register your organization and create an admin account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Organization Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Organization Details
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="orgName">
                    {t('settings.organizationName')} *
                  </Label>
                  <Input
                    id="orgName"
                    placeholder="e.g., Community Cultural Association"
                    value={formData.orgName}
                    onChange={(e) => handleChange('orgName', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="orgDescription">
                    {t('settings.organizationDescription')}
                  </Label>
                  <Input
                    id="orgDescription"
                    placeholder="Brief description of your organization"
                    value={formData.orgDescription}
                    onChange={(e) => handleChange('orgDescription', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currency">
                      <DollarSign className="inline h-4 w-4 mr-1" />
                      {t('settings.defaultCurrency')} *
                    </Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => handleChange('currency', value)}
                    >
                      <SelectTrigger id="currency" className="w-full">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent position="popper" className="max-h-60 overflow-y-auto">
                        {currencies.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} - {curr.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="language">
                      <Globe className="inline h-4 w-4 mr-1" />
                      {t('settings.defaultLanguage')} *
                    </Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => handleChange('language', value)}
                    >
                      <SelectTrigger id="language" className="w-full">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent position="popper" className="max-h-60 overflow-y-auto">
                        {languages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monthlyDues">
                    {t('settings.monthlyDuesAmount')}
                  </Label>
                  <Input
                    id="monthlyDues"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.monthlyDues}
                    onChange={(e) => handleChange('monthlyDues', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Set to 0 if you don&apos;t collect monthly dues
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
                  Admin Account
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="adminName">
                    {t('auth.fullName')} *
                  </Label>
                  <Input
                    id="adminName"
                    placeholder="John Doe"
                    value={formData.adminName}
                    onChange={(e) => handleChange('adminName', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor="adminEmail">
                    {t('auth.email')} *
                  </Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    placeholder="admin@organization.com"
                    value={formData.adminEmail}
                    onChange={(e) => handleChange('adminEmail', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor="adminPassword">
                    {t('auth.password')} *
                  </Label>
                  <div className="relative">
                    <Input
                      id="adminPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.adminPassword}
                      onChange={(e) => handleChange('adminPassword', e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Minimum 6 characters
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.createAccount')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link
                  to="/org-login"
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signIn')}
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
